import {
  crossResourceRule,
  type GrantRow,
  requiredSupplierPermissions,
} from "../../shared/rbac/crossResourceAvailability";
import type { Permission } from "../../shared/rbac/permissions";
import { grantHasExactPermission } from "../../shared/rbac/roles";
import type { DisconnectImpact } from "../components/DisconnectWarning";
import type { WorkflowPreview } from "../components/WorkflowPreviewList";

type ConnectorRef = {
  connectedAppId: string;
  connectedApp?: { roleGrantsAsScope: readonly GrantRow[] } | undefined;
};

type UsageRow = ConnectorRef & {
  refId: string;
  commit?:
    | {
        branch?:
          | {
              name: string;
              headCommitId: string | null;
              workflow?:
                | {
                    id: string;
                    name: string;
                    badgeSeed: string | null;
                    spaceId: string;
                    deletedAt: number | null;
                  }
                | undefined;
            }
          | undefined;
      }
    | undefined;
};

type GitRow = ConnectorRef & {
  refId: string;
  space?: { name: string; mode: string } | undefined;
};

type SpaceRow = {
  id: string;
  mode: string;
  roleGrantsAsScope: readonly GrantRow[];
};

// Client mirror of the server's group-removal usage drop for the space rule:
// per connector the group grants, a removed user loses the permissions they
// keep through no other route, and every shared space that still requires them
// for that user gets its usages of the connector dropped. Best-effort (e.g.
// unaware of tenant-admin allow_all); the server drop stays authoritative.
export function groupRemovalDisconnects({
  groupId,
  removedUserIds,
  usageRows,
  gitRows,
  spaces,
  userGroupMemberships,
}: {
  groupId: string;
  removedUserIds: readonly string[];
  usageRows: readonly UsageRow[];
  gitRows: readonly GitRow[];
  spaces: readonly SpaceRow[];
  userGroupMemberships: ReadonlyMap<string, readonly string[]>;
}): DisconnectImpact {
  const permissions = [
    ...new Set(
      crossResourceRule("space_uses_connected_apps").tiers.flatMap(
        (tier) => tier.supplierPermissions
      )
    ),
  ];
  const sharedSpaces = spaces.filter((s) => s.mode !== "personal");

  const connectorGrants = new Map<string, readonly GrantRow[]>();
  for (const row of [...usageRows, ...gitRows]) {
    if (!connectorGrants.has(row.connectedAppId) && row.connectedApp)
      connectorGrants.set(
        row.connectedAppId,
        row.connectedApp.roleGrantsAsScope
      );
  }

  const affectedSpacesByConnector = new Map<string, Set<string>>();
  for (const [connectedAppId, grants] of connectorGrants) {
    const affected = new Set<string>();
    for (const userId of removedUserIds) {
      const otherGroups = new Set(
        (userGroupMemberships.get(userId) ?? []).filter((g) => g !== groupId)
      );
      const reaches = (g: GrantRow) =>
        g.userId === userId ||
        (g.userGroupId != null && otherGroups.has(g.userGroupId));
      const lost = permissions.filter(
        (p) =>
          grants.some(
            (g) => g.userGroupId === groupId && grantHasExactPermission(g, p)
          ) && !grants.some((g) => reaches(g) && grantHasExactPermission(g, p))
      );
      if (lost.length === 0) continue;
      for (const space of sharedSpaces) {
        if (affected.has(space.id)) continue;
        if (
          space.roleGrantsAsScope.some(
            (g) =>
              reaches(g) &&
              requiredSupplierPermissions("space_uses_connected_apps", g).some(
                (p: Permission) => lost.includes(p)
              )
          )
        )
          affected.add(space.id);
      }
    }
    if (affected.size > 0)
      affectedSpacesByConnector.set(connectedAppId, affected);
  }

  const workflows = new Map<string, WorkflowPreview>();
  for (const row of usageRows) {
    const branch = row.commit?.branch;
    const workflow = branch?.workflow;
    if (!branch || !workflow) continue;
    if (branch.headCommitId !== row.refId || workflow.deletedAt != null)
      continue;
    if (
      !affectedSpacesByConnector.get(row.connectedAppId)?.has(workflow.spaceId)
    )
      continue;
    const existing = workflows.get(workflow.id);
    if (existing) {
      if (branch.name === "main") existing.draft = false;
    } else {
      workflows.set(workflow.id, {
        id: workflow.id,
        name: workflow.name,
        badgeSeed: workflow.badgeSeed,
        spaceId: workflow.spaceId,
        draft: branch.name !== "main",
      });
    }
  }

  const gitSpaceNames = new Set<string>();
  for (const row of gitRows) {
    if (!affectedSpacesByConnector.get(row.connectedAppId)?.has(row.refId))
      continue;
    if (row.space?.name) gitSpaceNames.add(row.space.name);
  }

  return {
    workflows: [...workflows.values()],
    gitSpaceNames: [...gitSpaceNames],
  };
}
